import React, { useMemo } from 'react';
import { Controller, type FieldErrors } from 'react-hook-form';
import { Select } from '@planne-software/uni/Select';

import { useTranslate } from '@src/core/hooks';
import type { PaymentInstallmentOption } from '@src/domains/payments';

import type { CardFormValues } from '../core/cardPaymentFormSchema';
import type { CardFormControl } from '../types';
import { Grid } from '@planne-software/uni/Grid';

import { cardFieldSize } from '../styles';

type InstallmentChoice = {
  label: string;
  value: number;
};

type InstallmentsFieldProps = {
  control: CardFormControl;
  errors: FieldErrors<CardFormValues>;
  installmentOptions: PaymentInstallmentOption[];
  visible: boolean;
  disabled: boolean;
};

export const InstallmentsField = (props: InstallmentsFieldProps) => {
  const { control, errors, installmentOptions, visible, disabled } = props;
  const { i18n } = useTranslate();

  const choices = useMemo<InstallmentChoice[]>(
    () => installmentOptions.map(({ label, division }) => ({ label, value: division })),
    [installmentOptions],
  );

  if (!visible) {
    return null;
  }

  return (
    <Grid size={cardFieldSize('full')} data-testid='card-form-installments'>
      <Controller
        name='installments'
        control={control}
        render={({ field }) => (
          <Select<InstallmentChoice>
            name={field.name}
            id={field.name}
            label={i18n('checkout_payment_card_installments')}
            required
            options={choices}
            value={choices.find((choice) => choice.value === field.value)}
            getOptionKey={(choice) => choice.value}
            onChange={(_event, selected) => {
              const choice = Array.isArray(selected) ? selected[0] : selected;

              if (choice && typeof choice === 'object') {
                field.onChange(choice.value);
              }
            }}
            onBlur={field.onBlur}
            renderSearch={null}
            disableClearable
            disabled={disabled}
            error={!!errors.installments}
            helperText={errors.installments?.message}
            size='medium'
          />
        )}
      />
    </Grid>
  );
};

export default InstallmentsField;
